import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, FileDown, TrendingUp, MousePointerClick, Heart } from "lucide-react";

const STATS = [
  { label: "META ADS ROAS", value: "3x-4x", color: "#00E699" },
  { label: "AVG. THUMBNAIL CTR", value: "5-7%", color: "#FFB800" },
  { label: "SOCIAL MEDIA GROWTH", value: "30-40%", color: "#00C2FF" },
];

const STAT_ICONS = [TrendingUp, MousePointerClick, Heart];

const EASE = [0.22, 1, 0.36, 1];

export default function HeroCenter({ stats }) {
  const items = stats?.length ? stats : STATS;
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const circleScrollY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spotX = useSpring(mx, { stiffness: 55, damping: 18 });
  const spotY = useSpring(my, { stiffness: 55, damping: 18 });
  const circleX = useSpring(useTransform(mx, (v) => v * 0.03), { stiffness: 60, damping: 16 });
  const circleMouseY = useSpring(useTransform(my, (v) => v * 0.03), { stiffness: 60, damping: 16 });

  const onHeroMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX - r.left - r.width / 2);
    my.set(e.clientY - r.top - r.height / 2);
  };

  return (
    <section id="top" ref={sectionRef} onMouseMove={onHeroMouseMove} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36 pb-20 sm:pb-24">
      <motion.div style={{ y: glowY }} className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20 blur-[140px] pointer-events-none">
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, #FF4D23 0%, transparent 70%)" }} />
      </motion.div>
      <motion.div
        className="absolute w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.13] blur-[120px]"
        style={{ x: spotX, y: spotY, left: "50%", top: "38%", marginLeft: -260, marginTop: -260, background: "radial-gradient(circle, #FF4D23 0%, transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-7 text-left">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-mono2 text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FF4D23]"
              data-testid="hero-overline"
            >
              DESIGN LEADER · BRAND &amp; MARKETING DESIGN · 9+ YEARS
            </motion.p>

            <h1
              className="font-display font-extrabold uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-5xl mt-6 sm:mt-8 select-none"
              data-testid="hero-title"
            >
              <span className="block overflow-hidden pb-1 -mb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
                  Creative Direction
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1 -mb-1">
                <motion.span className="block text-[#FF4D23]" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.28, ease: EASE }}>
                  &amp; Design That
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1 -mb-1">
                <motion.span className="block text-[#8A92A3]" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.41, ease: EASE }}>
                  Move Brands Forward.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
              className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#B6BDCB]"
              data-testid="hero-subtitle"
            >
              I'm Vijay Rajput—a multidisciplinary designer and design leader with 9+ years of experience
              building brand identities, campaigns and scalable creative systems. I combine visual craft,
              strategic thinking and team leadership to create work that connects with audiences and
              supports business growth.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="#work"
                data-testid="hero-cta-work"
                className="inline-flex items-center gap-2 bg-[#FF4D23] text-[#0A0B0E] font-bold px-7 py-3.5 rounded-full hover:bg-[#ff6a45] hover:scale-[1.03] transition-all"
              >
                Explore Selected Work <ArrowDown size={18} />
              </a>
              <a
                href="/Vijay_Rajput_Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-resume"
                className="inline-flex items-center gap-2 border border-[#3B4254] text-[#F5F6FA] font-semibold px-7 py-3.5 rounded-full hover:border-[#FF4D23] hover:text-[#FF4D23] transition-colors"
              >
                Download Resume <FileDown size={18} />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end mb-12 lg:mb-0">
            <motion.div
              style={{ y: circleScrollY }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
              className="relative"
            >
              <motion.div style={{ x: circleX, y: circleMouseY }} className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80">
                <div className="absolute -inset-3 rounded-full border border-dashed border-[#3B4254] animate-[spin_36s_linear_infinite] pointer-events-none" />
                <svg
                  viewBox="0 0 200 200"
                  className="absolute pointer-events-none animate-[spin_26s_linear_infinite]"
                  style={{ width: "140%", height: "140%", left: "-20%", top: "-20%" }}
                >
                  <defs>
                    <path id="heroCirclePath" d="M100,100 m-82,0 a82,82 0 1,1 164,0 a82,82 0 1,1 -164,0" />
                  </defs>
                  <text fill="#8A92A3" fontSize="11" letterSpacing="3.2" style={{ fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase" }}>
                    <textPath href="#heroCirclePath">
                      Creative Direction · Brand Design · Visual Storytelling ·
                    </textPath>
                  </text>
                </svg>
                <img
                  src="/vijay-photo.jpg"
                  alt="Vijay Rajput — Deputy Manager Design, Creative Leader"
                  data-testid="hero-photo"
                  className="w-full h-full rounded-full object-cover object-top border-4 border-[#0A0B0E] ring-2 ring-[#FF4D23]/50 shadow-[0_20px_60px_rgba(255,77,35,0.25)]"
                />
                <div
                  className="absolute -top-4 -left-8 sm:-left-12 backdrop-blur-xl bg-[#12141B]/90 border border-[#222634] rounded-xl px-4 py-3 animate-float-slow"
                  data-testid="hero-badge-experience"
                >
                  <p className="font-mono2 text-lg font-bold text-[#FF4D23]">9+ Years</p>
                  <p className="text-[10px] font-mono2 tracking-widest text-[#8A92A3] uppercase">Experience</p>
                </div>
                <div
                  className="absolute -bottom-4 -right-4 sm:-right-8 backdrop-blur-xl bg-[#12141B]/90 border border-[#222634] rounded-xl px-4 py-3 animate-float-slow"
                  style={{ animationDelay: "1.2s" }}
                  data-testid="hero-badge-degree"
                >
                  <p className="font-mono2 text-xl font-bold text-[#FFB800]">M.Des</p>
                  <p className="text-[10px] font-mono2 tracking-widest text-[#8A92A3] uppercase">Chandigarh University</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-14 lg:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4"
          data-testid="hero-stats"
        >
          {items.map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <motion.div
                key={`${s.label}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + i * 0.12, ease: EASE }}
                whileHover={{ y: -4, borderColor: "#3B4254" }}
                className="border border-[#222634] bg-[#12141B]/60 rounded-xl p-5 text-left"
                data-testid={`hero-stat-${i}`}
              >
                <Icon size={20} style={{ color: s.color }} />
                <p className="font-mono2 text-2xl sm:text-3xl font-bold mt-3" style={{ color: s.color }}>
                  {s.value}
                </p>
                <p className="text-[10px] sm:text-xs font-mono2 tracking-widest text-[#8A92A3] mt-1 uppercase">
                  {s.label}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
